// The waitlist page (docs/plans/waitlist.md): what Inkglish is, how it works, and a form for your email, twice (top
// and bottom). Redesigned 31 Oct 2026 in the light Paper look (direction A on the Waitlist canvas): Ink sits behind a
// card that plays Speak, Role play and Write, then the eight ways to practice, what Ink's AI does for you, how it
// works with Today as it is in the app, and Ink waving at the close.
// Not part of the signed-in app: web/waitlist.html. Once sign-up opens it is also the home page (web/home.html, open):
// Start free with Google in place of the forms, and Sign in in the top bar.
import { waitlistJoinSchema } from '@shared/waitlist';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import {
  BrandMark,
  Button,
  Delta,
  Icon,
  type IconName,
  ListItem,
  PlanBar,
  Tick,
  VoiceBars,
  weekBars,
} from '@/components';
import { startGoogleSignIn } from '@/features/auth/data';
import { ApiError } from '@/lib/api';
import { joinWaitlist, sourceFromLink } from './data';
import { InkFigure } from './Ink';

// Where the lesson videos are posted (video/PLAN.md).
const CHANNELS = [
  { name: 'YouTube', href: 'https://www.youtube.com/@inkglish' },
  { name: 'Instagram', href: 'https://www.instagram.com/inkglishapp/' },
  { name: 'Facebook', href: 'https://www.facebook.com/inkglish/' },
  { name: 'LinkedIn', href: 'https://www.linkedin.com/company/inkglish/' },
];

const SKILLS: { icon: IconName; name: string; sub: string }[] = [
  { icon: 'mic', name: 'Speak', sub: 'Answer a question out loud. Get a score and your words corrected.' },
  { icon: 'pen-line', name: 'Write', sub: 'Write an email or a short answer. See every change and why.' },
  { icon: 'messages-square', name: 'Role play', sub: 'Practice a chat, a call or an interview with Ink.' },
  { icon: 'headphones', name: 'Listen', sub: 'Clear voices at real speed, then a few quick questions.' },
  { icon: 'book', name: 'Words', sub: 'New words for your level.' },
  { icon: 'circle-play', name: 'Drills', sub: 'Quick practice of the phrases you need most.' },
  { icon: 'gamepad-2', name: 'Play', sub: 'Levels of short games.' },
  { icon: 'repeat', name: 'Review', sub: 'Your own mistakes come back until they stick.' },
];

const STEPS = [
  {
    name: 'Answer a real question',
    sub: 'Daily life, opinions, small talk, work. Speak for a minute or write a few lines. Ink picks questions for your goal and your level.',
  },
  {
    name: 'Ink fixes it in your own words',
    sub: 'A score out of 100, your sentence corrected, and a short reason in plain English. No grammar words without an example.',
  },
  {
    name: 'See that you are improving',
    sub: 'Every score answers one question: am I getting better? “Your grammar is up 6 this month.”',
  },
];

const DAY: { icon: IconName; name: string; sub: string }[] = [
  {
    icon: 'clock',
    name: '2 to 5 minutes each',
    sub: "A full day's plan takes about 15 minutes. Do one task or all of them.",
  },
  {
    icon: 'arrow-right',
    name: 'One clear next step',
    sub: 'Today shows the one thing to do next. You never have to decide where to start.',
  },
  {
    icon: 'calendar',
    name: 'Miss a day, keep your streak',
    sub: 'One free day a week. Wrong answers simply come back later.',
  },
];

// Today as the app shows it: a week of practice, minutes on each day.
const WEEK = [
  { label: 'Mon', minutes: 15 },
  { label: 'Tue', minutes: 20 },
  { label: 'Wed', minutes: 0 },
  { label: 'Thu', minutes: 25 },
  { label: 'Fri', minutes: 10 },
  { label: 'Sat', minutes: 30 },
  { label: 'Today', minutes: 12, today: true },
];

export default function WaitlistPage({ open = false }: { open?: boolean }) {
  // Shared by both forms: joining at the top also shows "You're on the list" at the bottom.
  const [joinedAs, setJoinedAs] = useState<string | null>(null);
  const form = { joinedAs, onJoined: setJoinedAs, onChangeEmail: () => setJoinedAs(null) };

  return (
    <div className="wl" id="top">
      <header className="wl-wrap wl-nav">
        <Brand />
        <nav className="wl-navlinks" aria-label="Page">
          {open ? (
            <>
              <a className="wl-navlink" href="/lessons">
                Free lessons
              </a>
              <a className="wl-navlink" href="/pricing">
                Pricing
              </a>
            </>
          ) : (
            <>
              <a className="wl-navlink" href="#can">
                What you can do
              </a>
              <a className="wl-navlink" href="#ai">
                How Ink helps
              </a>
            </>
          )}
          {open ? (
            <a className="btn secondary sm" href="/signin" data-umami-event="Sign in">
              Sign in
            </a>
          ) : (
            <a className="btn secondary sm" href="#join">
              Join the waitlist
            </a>
          )}
        </nav>
      </header>

      <main id="main">
        <section className="wl-wrap wl-hero" aria-labelledby="hero-title">
          <div className="wl-hero-top">
            {!open && (
              <p className="wl-soon">
                <span className="wl-soon-dot" aria-hidden="true" />
                Opening in November 2026
              </p>
            )}
            <h1 className="wl-h1" id="hero-title">
              Meet Ink, your <span className="highlight wl-sweep">AI English teacher.</span>
            </h1>
            <p className="wl-lead">
              Practice the English you use every day: small talk, opinions, travel, work. Ink listens, fixes your
              English in your own words, and shows you that you are getting better.
            </p>
          </div>
          <div className="wl-hero-form">
            {open ? (
              <>
                <div className="wl-starts">
                  <StartButton />
                  <a className="btn secondary lg" href="/lessons" data-umami-event="Free lessons">
                    Try a free lesson
                  </a>
                </div>
                <p className="wl-note">
                  Free to start. <a href="/pricing">See plans and prices</a>
                </p>
              </>
            ) : (
              <JoinForm id="top" {...form} />
            )}
          </div>
          <Stage />
        </section>

        <section className="wl-section" id="can" aria-labelledby="can-title">
          <div className="wl-wrap">
            <p className="wl-eyebrow">What you can do</p>
            <h2 className="wl-h2" id="can-title">
              Eight ways to practice. <span className="highlight">One plan a day.</span>
            </h2>
            <p className="wl-p wl-intro">
              Ink builds each day's plan from your goal, your job and your last mistakes. You just start.
            </p>
            <ul className="wl-ways">
              {SKILLS.map((s) => (
                <li className="wl-way wl-rise" key={s.name}>
                  <span className="wl-gl">
                    <Icon name={s.icon} />
                  </span>
                  <div>
                    <p className="wl-row-t">{s.name}</p>
                    <p className="wl-row-sub">{s.sub}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="wl-section" id="ai" aria-labelledby="ai-title">
          <div className="wl-wrap">
            <p className="wl-eyebrow">For people who already use AI</p>
            <h2 className="wl-h2" id="ai-title">
              <span className="highlight">You already use AI.</span> Now let it teach you English.
            </h2>
            <p className="wl-p wl-intro">
              A chat tool can write the email for you. Ink helps you write it yourself, and shows you that you are
              getting better.
            </p>
            <AiPoints />
          </div>
        </section>

        <section className="wl-section" id="how" aria-labelledby="how-title">
          <div className="wl-wrap">
            <div className="wl-split">
              <div>
                <p className="wl-eyebrow">How it works</p>
                <h2 className="wl-h2" id="how-title">
                  Five minutes a day, and <span className="highlight">you can see it working.</span>
                </h2>
                <ol className="wl-how-steps">
                  {STEPS.map((s, i) => (
                    <li className="wl-rise" key={s.name}>
                      <span className="wl-num" aria-hidden="true">
                        {i + 1}
                      </span>
                      <div>
                        <h3 className="wl-h3">{s.name}</h3>
                        <p className="wl-p">{s.sub}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
              <TodayPreview />
            </div>
            <ul className="wl-points">
              {DAY.map((d) => (
                <li className="wl-rise" key={d.name}>
                  <span className="wl-gl">
                    <Icon name={d.icon} />
                  </span>
                  <div>
                    <p className="wl-row-t">{d.name}</p>
                    <p className="wl-row-sub">{d.sub}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="wl-cta" id="join" aria-labelledby="join-title">
          <div className="wl-wrap wl-cta-in">
            <div className="wl-cta-ink" aria-hidden="true">
              <span className="wl-glow" />
              <InkFigure wave />
            </div>
            <h2 className="wl-h2 wl-cta-title" id="join-title">
              {open ? (
                <>
                  Start practicing <span className="highlight">with Ink today.</span>
                </>
              ) : (
                <>
                  Be one of the first to <span className="highlight">practice with Ink.</span>
                </>
              )}
            </h2>
            <p className="wl-p wl-intro">
              {open
                ? 'Free to start. Sign in with Google, answer four questions, and Ink builds your first plan.'
                : "Inkglish opens in November 2026. Join the waitlist and we'll email you on the day it opens."}
            </p>
            {open ? <StartButton /> : <JoinForm id="end" {...form} />}
            <p className="wl-follow">
              {open ? 'Learn with Ink on' : 'While you wait, learn with Ink on'}{' '}
              {CHANNELS.map((c, i) => (
                <span key={c.name}>
                  <a href={c.href}>{c.name}</a>
                  {i < CHANNELS.length - 2 ? ', ' : i === CHANNELS.length - 2 ? ' and ' : '.'}
                </span>
              ))}
            </p>
          </div>
        </section>
      </main>

      <footer className="wl-foot">
        <div className="wl-wrap wl-foot-in">
          <Brand />
          <nav className="wl-foot-links" aria-label="Footer">
            {open && (
              <a className="wl-navlink" href="/lessons">
                Free lessons
              </a>
            )}
            <a className="wl-navlink" href="/pricing">
              Pricing
            </a>
            <a className="wl-navlink" href="/privacy">
              Privacy
            </a>
            <a className="wl-navlink" href="/terms">
              Terms
            </a>
            <a className="wl-navlink" href="/refunds">
              Refunds
            </a>
            <a className="wl-navlink" href="mailto:hello@inkglish.com">
              Contact
            </a>
            <span className="wl-foot-copy">© 2026 Inkglish</span>
          </nav>
        </div>
      </footer>
    </div>
  );
}

/** Open sign-up: one way in, Google, as on the sign-in page. A start that fails opens sign-in, which says so. */
function StartButton() {
  const [pending, setPending] = useState(false);
  const start = async () => {
    setPending(true);
    try {
      await startGoogleSignIn();
    } catch {
      window.location.assign('/signin?error=start_failed');
    }
  };
  return (
    <div className="wl-start">
      {/* Umami counts clicks on whatever carries data-umami-event; Button doesn't pass it through. */}
      <span data-umami-event="Start free">
        <Button size="lg" onClick={start} disabled={pending}>
          {pending ? 'Opening Google' : 'Start free with Google'}
        </Button>
      </span>
    </div>
  );
}

function Brand() {
  return (
    <a className="wl-brand" href="#top">
      <span className="wl-tile" aria-hidden="true">
        <BrandMark />
      </span>
      <span>inkglish</span>
    </a>
  );
}

type JoinFormProps = {
  id: string;
  joinedAs: string | null;
  onJoined: (email: string) => void;
  onChangeEmail: () => void;
};

/** Your email, and the one amber button. Once joined, it says so in its place. */
function JoinForm({ id, joinedAs, onJoined, onChangeEmail }: JoinFormProps) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const done = useRef<HTMLDivElement>(null);
  const justJoined = useRef(false);

  // The form that was used moves focus to its confirmation, so a screen reader hears it.
  useEffect(() => {
    if (joinedAs !== null && justJoined.current) done.current?.focus();
    justJoined.current = false;
  }, [joinedAs]);

  if (joinedAs !== null) {
    return (
      <div className="wl-joined" role="status" tabIndex={-1} ref={done}>
        <span className="wl-check" aria-hidden="true">
          <Icon name="check" />
        </span>
        <div>
          <p className="wl-joined-title">You're on the list.</p>
          <p className="wl-note">We'll email {joinedAs} when Inkglish opens.</p>
          <button className="wl-linkbtn" type="button" onClick={onChangeEmail}>
            Use a different email
          </button>
        </div>
      </div>
    );
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const website = new FormData(event.currentTarget).get('website');
    const join = waitlistJoinSchema.safeParse({
      email,
      source: sourceFromLink(window.location.search),
      website: typeof website === 'string' && website ? website : undefined,
    });
    if (!join.success) {
      setError(
        email.trim()
          ? 'That email address looks incomplete. Check it and try again.'
          : 'Enter your email address, like name@example.com.',
      );
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await joinWaitlist(join.data);
      justJoined.current = true;
      onJoined(join.data.email);
    } catch (e) {
      if (!(e instanceof ApiError)) setError("Couldn't reach Inkglish. Check your connection and try again.");
      else setError(e.status >= 500 && e.requestId ? `${e.message} Reference: ${e.requestId}` : e.message);
    } finally {
      setBusy(false);
    }
  }

  const input = `email-${id}`;
  return (
    <form className="wl-form" onSubmit={submit} noValidate>
      <label className="label" htmlFor={input}>
        Your email
      </label>
      <div className="wl-form-row">
        <input
          className="input wl-input"
          id={input}
          name="email"
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="name@example.com"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setError(null);
          }}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${input}-error` : `${input}-note`}
        />
        <Button type="submit" size="lg" disabled={busy}>
          Join the waitlist
        </Button>
      </div>
      {/* People never see this field; a bot that fills it in is not saved. */}
      <div className="wl-hp" aria-hidden="true">
        <label>
          Website
          <input name="website" type="text" tabIndex={-1} autoComplete="off" />
        </label>
      </div>
      {error && (
        <p className="wl-error" id={`${input}-error`} role="alert">
          {error}
        </p>
      )}
      <p className="wl-note" id={`${input}-note`}>
        One email when Inkglish opens, and nothing else. <a href="/privacy">How we use your email</a>
      </p>
    </form>
  );
}

/** The hero's picture: Ink behind a card that plays Speak, Role play and Write on one 15-second loop. She greets you,
 * then says one line on each result; the card is drawn between her body and her hands. */
function Stage() {
  return (
    <figure
      className="wl-stage"
      aria-label="Ink sits behind a card that plays three examples in turn: a spoken answer scored 74 with one tense fixed, a role play where Ink plays your manager and you reach both goals, and an email with three fixes scored 81."
    >
      <div className="wl-ink" aria-hidden="true">
        <div className="wl-me">
          <span className="wl-glow" />
          <InkFigure part="body" />
          <InkFigure part="arms" />
        </div>
        <div className="wl-says">
          <p className="wl-say s0">Hi, I'm Ink. Answer out loud. I'm listening.</p>
          <p className="wl-say s1">Clear answer. One tense to fix.</p>
          <p className="wl-say s2">Polite and clear. You offered a plan.</p>
          <p className="wl-say s3">Three small fixes, each with a reason.</p>
        </div>
      </div>
      <div className="wl-card" aria-hidden="true">
        <ol className="wl-tabs">
          <li className="t1">
            <span className="wl-tab">
              <Icon name="mic" />
              Speak
            </span>
            <i />
          </li>
          <li className="t2">
            <span className="wl-tab">
              <Icon name="messages-square" />
              Role play
            </span>
            <i />
          </li>
          <li className="t3">
            <span className="wl-tab">
              <Icon name="pen-line" />
              Write
            </span>
            <i />
          </li>
        </ol>
        <div className="wl-scenes">
          <div className="wl-sc wl-sc1">
            <p className="wl-sc-eye">Speak · Explain a delay</p>
            <p className="wl-sc-q">Your manager asks why the report is late. What do you say?</p>
            <div className="wl-wave">
              <div className="live">
                {LIVE.map((bar, i) => (
                  <span
                    // biome-ignore lint/suspicious/noArrayIndexKey: bars are positional.
                    key={i}
                    style={{
                      height: `${bar.height}%`,
                      animationDuration: `${bar.duration}ms`,
                      animationDelay: `${bar.delay}ms`,
                    }}
                  />
                ))}
              </div>
              <div className="settled">
                {SETTLED.map((height, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: bars are positional.
                  <span key={i} style={{ height: `${height}%` }} />
                ))}
              </div>
            </div>
            <div className="wl-status">
              <span className="rec">
                <span className="rec-dot" />
                Recording <span className="wl-timer" />
              </span>
              <span className="done">Your answer · 0:24</span>
            </div>
            <div className="wl-score">
              <span className="sn" />
              <span className="of">/100</span>
              <span className="dl">+6 vs your average</span>
            </div>
            <div className="wl-fix">
              <p className="now">
                I <span className="u">sent</span> the first part yesterday.
              </p>
              <p className="why">With “yesterday”, use the past simple, not “have sent”.</p>
            </div>
          </div>

          <div className="wl-sc wl-sc2">
            <p className="wl-sc-eye">Role play · Ink plays your manager</p>
            <ul className="wl-goals">
              <li className="g1">
                <span className="gk">
                  <Icon name="check" />
                </span>
                Say what is possible
              </li>
              <li className="g2">
                <span className="gk">
                  <Icon name="check" />
                </span>
                Offer a plan
              </li>
            </ul>
            <div className="wl-chat">
              <p className="bubble ai c1">
                <span className="who">Manager</span>The client wants the report by Friday. Can we do it?
              </p>
              <div className="slot">
                <p className="bubble me typing c-typing">
                  <span />
                  <span />
                  <span />
                </p>
                <p className="bubble me c2">
                  <span className="who">You</span>Friday is tight. I can send the first part on Thursday and the rest on
                  Monday.
                </p>
              </div>
              <p className="bubble ai c3">
                <span className="who">Manager</span>OK, that works. Thanks for being clear.
              </p>
            </div>
          </div>

          <div className="wl-sc wl-sc3">
            <p className="wl-sc-eye">Write · Reply to a client</p>
            <div className="wl-mail">
              <p className="ln l1">Hi Maria,</p>
              <p className="ln l2">
                Thanks for waiting. I{' '}
                <span className="x">
                  <s>have went</s>
                  <b>went</b>
                </span>{' '}
                through the numbers and they{' '}
                <span className="x x2">
                  <s>looks</s>
                  <b>look</b>
                </span>{' '}
                fine.
              </p>
              <p className="ln l3">
                <span className="x x3">
                  <s>I send</s>
                  <b>I'll send</b>
                </span>{' '}
                you the final file tomorrow.
              </p>
            </div>
            <div className="wl-score">
              <span className="sn" />
              <span className="of">/100</span>
              <span className="dl">+4 vs your average</span>
            </div>
            <p className="wl-mail-note">
              <b>3 fixes</b>, each with a short reason you can open.
            </p>
          </div>
        </div>
      </div>
    </figure>
  );
}

// The Speak example's two waves: your voice while you speak (each bar moves on its own) and your answer once it
// settles. MINI is the small live wave on "It listens to you".
const LIVE = Array.from({ length: 44 }, (_, i) => ({
  height: Math.round(34 + Math.abs(Math.sin(i * 2.3) * 0.6 + Math.sin(i * 0.8) * 0.4) * 66),
  duration: 380 + ((i * 137) % 460),
  delay: -((i * 211) % 900),
}));
const SETTLED = Array.from({ length: 44 }, (_, i) => {
  if ([13, 14, 29, 30].includes(i)) return 6;
  const envelope = Math.abs(Math.sin(i * 0.9)) * (0.5 + 0.5 * Math.abs(Math.sin(i * 0.21 + 0.5)));
  return Math.round(16 + envelope * 84);
});
const MINI = Array.from({ length: 18 }, (_, i) => ({
  height: Math.round(30 + Math.abs(Math.sin(i * 1.7)) * 70),
  duration: 420 + ((i * 97) % 380),
  delay: -((i * 173) % 700),
}));
const RISE = [30, 42, 38, 58, 66, 80, 100];

/** What Ink's AI does for you, each with a small picture of it at work. */
function AiPoints() {
  return (
    <ul className="wl-ai-grid">
      <li className="wl-ai wl-rise">
        <div className="wl-ai-vis" aria-hidden="true">
          <div className="wl-mini-wave">
            {MINI.map((bar, i) => (
              <span
                // biome-ignore lint/suspicious/noArrayIndexKey: bars are positional.
                key={i}
                style={{
                  height: `${bar.height}%`,
                  animationDuration: `${bar.duration}ms`,
                  animationDelay: `${bar.delay}ms`,
                }}
              />
            ))}
          </div>
        </div>
        <h3 className="wl-h3">It listens to you</h3>
        <p className="wl-p">
          Speak out loud. Ink hears your words, your pauses and your sounds, and scores your answer out of 100.
        </p>
      </li>
      <li className="wl-ai wl-rise">
        <div className="wl-ai-vis" aria-hidden="true">
          <p className="wl-mini-fix">
            I <s>have sent</s>
            <b>sent</b> it yesterday.
          </p>
        </div>
        <h3 className="wl-h3">It keeps your words</h3>
        <p className="wl-p">Ink fixes only what is wrong in your own sentence, with a short reason in plain English.</p>
      </li>
      <li className="wl-ai wl-rise">
        <div className="wl-ai-vis" aria-hidden="true">
          <div className="wl-mini-chat">
            <span className="bubble ai">Can we finish by Friday?</span>
            <span className="bubble me">Friday is tight. Thursday works.</span>
          </div>
        </div>
        <h3 className="wl-h3">It plays the other person</h3>
        <p className="wl-p">
          A friend, a shop, your manager, an interviewer. Talk or type. Then see which goals you reached.
        </p>
      </li>
      <li className="wl-ai wl-rise">
        <div className="wl-ai-vis" aria-hidden="true">
          <div className="wl-mini-up">
            {RISE.map((h, i) => (
              <i key={h} style={{ height: `${h}%`, animationDelay: `${i * 80}ms` }} />
            ))}
            <b className="wl-up num">+6</b>
          </div>
        </div>
        <h3 className="wl-h3">It remembers for you</h3>
        <p className="wl-p">
          Your mistakes come back until they stick. Every score shows the change against your average.
        </p>
      </li>
    </ul>
  );
}

/** Today as it is in the app: the plan with its bar, the next step, and the week with its minutes. */
function TodayPreview() {
  const minutes = WEEK.reduce((sum, d) => sum + d.minutes, 0);
  return (
    <div className="wl-today wl-rise" aria-hidden="true">
      <div className="wl-today-head">
        <b>Today's plan</b>
        <span className="num">1 of 2 done · about 2 min left</span>
      </div>
      <PlanBar label="1 of 2 done" steps={['done', 'now']} />
      <div className="wl-today-steps">
        <ListItem
          lead={<Tick done />}
          title="Speak"
          sub="Explain a delay to your manager"
          trail={
            <span className="wl-today-score num">
              <b>74</b>
              <Delta value={6} />
            </span>
          }
        />
        <ListItem lead={<Tick next />} title="Review 3 mistakes" sub="Next · 2 min" />
      </div>
      <div className="wl-today-week">
        <div className="wl-today-stats">
          <p>
            <span>This week</span>
            <b className="num">{`${Math.floor(minutes / 60)}h ${minutes % 60}m`}</b>
            <span className="num">
              <span className="wl-up">+24m</span> vs last week
            </span>
          </p>
          <p>
            <span>Streak</span>
            <b className="num">6 days</b>
            <span>Your longest yet</span>
          </p>
        </div>
        <VoiceBars
          week
          tall
          label="Minutes practiced each day this week"
          bars={weekBars(WEEK).map((b, i) => {
            const m = WEEK[i]?.minutes ?? 0;
            return m > 0 ? { ...b, top: String(m) } : b;
          })}
        />
      </div>
    </div>
  );
}
